import { ModelBase } from './base';
import { BasicProfile } from './profile';

export interface LeadFormQuestionOption {
  id: string;
  title: string;
  sort: number;
  // only 1 opton can be ideal
  ideal?: boolean;
}

export enum LeadFormQuestionType {
  SingleChoice = 'SINGLE_CHOICE',
  MultipleChoice = 'MULTIPLE_CHOICE',
  Text = 'TEXT',
}

export interface LeadFormSingleChoiceQuestion {
  id: string;
  type: LeadFormQuestionType.SingleChoice,
  title: string;
  sort: number;
  // at least 1 option, up to 5
  options: LeadFormQuestionOption[];
}

export interface LeadFormMultipleChoiceQuestion {
  id: string;
  type: LeadFormQuestionType.MultipleChoice,
  title: string;
  sort: number;
  // at least 2 option, up to 5
  options: LeadFormQuestionOption[];
}

export interface LeadFormTextQuestion {
  id: string;
  type: LeadFormQuestionType.Text,
  title: string;
  sort: number;
}

export type LeadFormQuestion =
  | LeadFormSingleChoiceQuestion
  | LeadFormMultipleChoiceQuestion
  | LeadFormTextQuestion;

export enum LeadFormTriggerActionType {
  Button = 'BUTTON',
  MessageMe = 'MESSAGE_ME',
  ContactCard = 'CONTACT_CARD',
  DigitalProduct = 'DIGITAL_PRODUCT',
  Course = 'COURSE',
  Calendar = 'CALENDAR',
  // meeting type in availability
  MeetingType = 'MEETING_TYPE',
  RealEstateListing = 'REAL_ESTATE_LISTING',
}

export interface LeadFormTriggerAction {
  type: LeadFormTriggerActionType;
  id: string;
}

export enum LeadFormActionOnQualified {
  UnlockRegistration = 'UNLOCK_REGISTRATION',
  UnlockItem = 'UNLOCK_ITEM',
  RedirectToLink = 'REDIRECT_TO_LINK',
  TriggerAutomation = 'TRIGGER_AUTOMATION',
}

export enum LeadFormActionOnUnqualified {
  ShowPoliteMessage = 'SHOW_POLITE_MESSAGE',
  SuggestDifferentLink = 'SUGGEST_DIFFERENT_LINK',
}

// stores/{storeId}/leadForms/{autoId}
export interface LeadForm extends ModelBase {
  title: string;
  description?: string;
  active: boolean;
  // 1 at least
  // 5 at most
  questions: LeadFormQuestion[];

  // by default all questions appear together
  showQuestionsInSequence?: boolean;

  // aitomatically grant access to customers who match your criteria
  autoQualify?: boolean;
  // minimum number of ideal answers requires
  minIdealAnswersRequired?: number;
  // action on qualified
  actionOnQualified?: LeadFormActionOnQualified;
  // action on unqualified
  actionOnUnqualified?: LeadFormActionOnUnqualified;
  // used when actionOnQualified is RedirectToLink
  redirectLink?: string;
  // used when actionOnUnqualified is ShowPoliteMessage
  politeMessageTitle?: string;
  politeMessageHtml?: string;
  politeMessage?: string;
  // used when actionOnUnqualified is SuggestDifferentLink
  differentLink?: string;

  // require people to complete this form before they contitue
  triggerActions?: LeadFormTriggerAction[];
  // for searching, each item is ${type}_${id}
  triggerActionKeys?: string[];
}

export interface SelectedLeadFormQuestionOption extends LeadFormQuestionOption {
  selected?: boolean;
}

export interface AnsweredLeadFormSingleChoiceQuestion extends LeadFormSingleChoiceQuestion {
  options: SelectedLeadFormQuestionOption[];
}

export interface AnsweredLeadFormMultipleChoiceQuestion extends LeadFormMultipleChoiceQuestion {
  options: SelectedLeadFormQuestionOption[];
}

export interface AnsweredLeadFormTextQuestion extends LeadFormTextQuestion {
  text?: string;
}

export type AnsweredLeadFormQuestion =
  | AnsweredLeadFormSingleChoiceQuestion
  | AnsweredLeadFormMultipleChoiceQuestion
  | AnsweredLeadFormTextQuestion;

// stores/{storeId}/leadForms/{autoId}/submissions/{autoId}
export interface LeadFormSubmission extends ModelBase {
  // questions answered and options selected
  answers: AnsweredLeadFormQuestion[];
  // user id and profile only for the signed users
  userId?: string;
  userProfile?: {
    firstName: string;
    lastName: string;
    username?: string;
    avatar?: string;
    phone?: string;
    email?: string;
  };
  // contact info only for the anonymous users
  contact?: {
    name?: string;
    phone?: string;
    email?: string;
  }
  // consent to be contacted
  consent?: boolean;
  // consent for marketing offer
  offerConsent?: boolean;
}

// stores/{storeId}/leadForms/{autoId}/contacts/{autoId}
export interface LeadFormContact extends ModelBase {
  userId?: string;
  userProfile?: BasicProfile;
  phone?: string;
  email: string;
  consent: boolean;
}
